import { z } from 'zod';
import { MAX_TOKEN_LIMIT, tokenLimitSchema } from '../../server/src/modules/gateway/contracts';
import { SERVER_PROVIDER_ID } from './providers/ids';
import { providerPurpose } from './providers/types';
import type { Conversation, Preset, ProviderConfig, RoleModelConfig } from './types';
export type { RoleModelConfig } from './types';

const settingsSchema = z.object({
  maxContext: tokenLimitSchema,
  maxOutputTokens: tokenLimitSchema,
  temperature: z.number().min(0).max(2),
  topP: z.number().min(0).max(1),
  thinkingLevel: z.enum(['off', 'low', 'medium', 'high']),
  stream: z.boolean().default(true),
}).strict();
const configSchema = z.object({
  providerId: z.string().min(1).max(160).refine(value => value.trim() !== ''),
  model: z.string().min(1).max(256).refine(value => value.trim() !== ''),
  settings: settingsSchema,
}).strict();

export function parseRoleModelConfig(value: unknown): RoleModelConfig {
  const parsed = configSchema.safeParse(value);
  if (parsed.success) return parsed.data;
  const paths = [...new Set(parsed.error.issues.map(issue => issue.path.join('.') || 'modelConfig'))];
  const token = paths.find(path => path === 'settings.maxContext' || path === 'settings.maxOutputTokens');
  if (token) throw new Error(`角色模型 ${token} 必须是 1 到 ${MAX_TOKEN_LIMIT} 之间的整数`);
  throw new Error(`角色模型配置无效：${paths.join('、')}，请检查提供商、模型和参数`);
}

export function validateRoleModelLimits(config: RoleModelConfig, limits?: { contextTokens: number; maxOutputTokens: number }): void {
  const parsed = parseRoleModelConfig(config);
  if (parsed.providerId !== SERVER_PROVIDER_ID || !limits) return;
  if (parsed.settings.maxContext > limits.contextTokens) throw new Error(`角色模型最大上下文不能超过官方模型上限 ${limits.contextTokens} token`);
  if (parsed.settings.maxOutputTokens > limits.maxOutputTokens) throw new Error(`角色模型最大输出不能超过官方模型上限 ${limits.maxOutputTokens} token`);
}

/** Resolve a request without rewriting saved conversation settings or global defaults. */
export function resolveRoleConversation(preset: Preset | undefined, conversation: Conversation | undefined, providers: readonly ProviderConfig[]): Conversation | undefined {
  if (preset?.modelConfig === undefined) return conversation;
  const config = parseRoleModelConfig(preset.modelConfig);
  if (config.providerId !== SERVER_PROVIDER_ID) {
    const provider = providers.find(row => row.id === config.providerId);
    if (!provider?.enabled) throw new Error('角色模型提供商已删除或停用，请重新配置角色模型');
    if (providerPurpose(provider) !== 'chat') throw new Error('角色模型必须使用文字模型提供商');
  }
  return {
    ...(conversation ?? { id: `role-model-${preset.id}`, title: preset.name, createdAt: 0, updatedAt: 0, messageCount: 0 }),
    presetId: preset.id,
    providerId: config.providerId,
    model: config.model,
    modelKind: 'text',
    settings: { ...config.settings },
  };
}
